import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import { isMainInventoryLocation } from "../../purchasing/utils/receivingLocationPolicy";

const SAFE_CONFIRMATION_TEXT = "RECONCILE INVENTORY LOCATIONS";
const PAGE_SIZE = 25;
const MANUAL_GRID_LIMIT = 2000;
const LEGACY_TARGET_KEY = "legacy_unallocated";

export function buildInventoryReconciliationPreviewParams({
  businessUnitId,
  offset = 0,
  limit = PAGE_SIZE,
  includeAll = false,
}) {
  return {
    business_unit_id: Number(businessUnitId),
    limit,
    offset: Math.max(Number(offset) || 0, 0),
    ...(includeAll ? { include_all: true } : {}),
  };
}

function isSafeBatchPlan(plan) {
  return plan?.status === "ready" && !plan.rounding_correction;
}

function isMainAuditLocation(row) {
  return isMainInventoryLocation({ is_main: row.is_main, code: row.location_code || row.code });
}

function blockUnsafeMainProposal(plan) {
  if (plan.status !== "ready") return plan;
  const locations = plan.locations || [];
  if (!locations.some((row) => row.is_active !== false && !isMainAuditLocation(row)) ||
      !locations.some((row) => isMainAuditLocation(row) && Number(row.target_qty) > Number(row.current_qty))) {
    return plan;
  }
  return { ...plan, status: "blocked", rounding_correction: null, blockers: [...(plan.blockers || []), {
    code: "unsafe_main_increase",
    message: "This proposal puts stock into Main while other locations are active. Update the server and audit the business unit again.",
  }] };
}

export function mergeReconciliationPageSelections({
  selectedPlansById = {},
  pagePlans = [],
  executionMode,
  preserveSelection = true,
  autoSelectReady = true,
  maxItems = 50,
  pageOffset = 0,
}) {
  const next = preserveSelection ? { ...selectedPlansById } : {};

  for (const [index, plan] of pagePlans.entries()) {
    const itemId = String(Number(plan?.item_id));
    if (Object.prototype.hasOwnProperty.call(next, itemId)) {
      next[itemId] = { ...plan, selection_offset: pageOffset + index };
    }
  }

  if (executionMode === "safe_reconciliation" && autoSelectReady) {
    for (const [index, plan] of pagePlans.entries()) {
      if (!isSafeBatchPlan(plan)) continue;
      const itemId = String(Number(plan.item_id));
      if (Object.prototype.hasOwnProperty.call(next, itemId)) continue;
      if (Object.keys(next).length >= maxItems) break;
      next[itemId] = { ...plan, selection_offset: pageOffset + index };
    }
  }

  return next;
}

export function canApplyLoadedReconciliationPlans({
  selectedPlanCount,
  reason,
  minimumReasonLength,
  confirmation,
  confirmationText,
  manualSelectionsValid,
  manualMode,
  applying,
}) {
  const baseRequirementsMet =
    selectedPlanCount > 0 &&
    selectedPlanCount <= 50 &&
    manualSelectionsValid &&
    !applying;

  if (!baseRequirementsMet) return false;
  if (manualMode) return true;

  return (
    String(reason || "").trim().length >= minimumReasonLength &&
    confirmation === confirmationText
  );
}

function formatQty(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  }).format(number);
}

function signedQty(value) {
  const number = Number(value || 0);
  if (Math.abs(number) < 0.00000001) return "0";
  return `${number > 0 ? "+" : ""}${formatQty(number)}`;
}

function statusCopy(status) {
  if (status === "ready") return "Ready";
  if (status === "reconciled") return "Already reconciled";
  return "Blocked";
}

function blockerCopy(blocker) {
  if (!blocker) return "Unknown blocker";
  const location = blocker.location_name ? `${blocker.location_name}: ` : "";
  if (blocker.code === "missing_location_anchor") {
    return `${location}non-zero stock has no completed count anchor.`;
  }
  if (blocker.code === "legacy_unallocated_balance") {
    return `Global and location totals differ by ${signedQty(blocker.quantity)}.`;
  }
  if (blocker.code === "unattributed_movement_after_anchor") {
    const movementIds = Array.isArray(blocker.movement_ids)
      ? blocker.movement_ids.filter(Boolean)
      : blocker.movement_id
        ? [blocker.movement_id]
        : [];
    const movements = movementIds.length
      ? `Movement${movementIds.length === 1 ? "" : "s"} ${movementIds.map((id) => `#${id}`).join(", ")}`
      : "One or more movements";
    return `${movements} cannot be reconciled with either physical or Legacy / Unallocated stock (${signedQty(blocker.quantity)}).`;
  }
  if (blocker.code === "negative_reconstructed_balance") {
    return `${location}the reconstructed balance would be negative.`;
  }
  if (blocker.code === "canonical_global_mismatch") {
    return `Stored global stock is ${formatQty(blocker.stored_global_qty)}, but the FIFO ledger replay ends at ${formatQty(blocker.canonical_global_qty)}. An exact manual reset is required.`;
  }
  if (blocker.code === "location_ledger_unavailable") {
    return "The immutable location ledger is not installed yet.";
  }
  return blocker.message || blocker.code || "Unknown blocker";
}

function warningCopy(warning) {
  if (!warning) return "Unverified location balance preserved.";
  const location = warning.location_name ? `${warning.location_name}: ` : "";
  if (warning.code === "unanchored_location_preserved") {
    return `${location}no completed count was found; the existing balance is preserved.`;
  }
  if (warning.code === "legacy_unallocated_preserved") {
    return `${signedQty(warning.quantity)} remains in Legacy / Unallocated instead of being invented in a physical location.`;
  }
  if (warning.code === "legacy_unallocated_defaulted_main") {
    return `${signedQty(warning.quantity)} is assigned to ${warning.location_name || "Main Location"} under the default-location rule.`;
  }
  if (warning.code === "unattributed_movements_reclassified") {
    const movementIds = Array.isArray(warning.movement_ids)
      ? warning.movement_ids.filter(Boolean)
      : [];
    const suffix = movementIds.length
      ? ` Movements: ${movementIds.map((id) => `#${id}`).join(", ")}.`
      : "";
    return `${signedQty(warning.quantity)} is retained as Legacy / Unallocated.${suffix}`;
  }
  return warning.message || warning.code || "Unverified location balance preserved.";
}

function unitName(plan) {
  return plan?.item?.unit?.name || plan?.item?.unit?.code || "main unit";
}

function RoundingCorrection({ plan, disabled, onApply }) {
  const [locationId, setLocationId] = useState("");
  const { quantity, eligible_location_ids } = plan.rounding_correction;
  const eligible = (plan.locations || []).filter((location) =>
    location.is_active !== false && !isMainAuditLocation(location) && eligible_location_ids.includes(Number(location.location_id))
  );
  const location = eligible.find((row) => String(row.location_id) === locationId);
  return (
    <div className="ilrRounding">
      <label className="ilrField">
        <span className="ilrLabel">Rounding location</span>
        <select className="ilrSelect" value={locationId} disabled={disabled}
          onChange={(event) => setLocationId(event.target.value)}>
          <option value="">Choose a stock location</option>
          {eligible.map((row) => (
            <option key={row.location_id} value={row.location_id}>
              {row.location_name} ({formatQty(row.current_qty)} {unitName(plan)})
            </option>
          ))}
        </select>
      </label>
      <div aria-live="polite">
        <div>Legacy / Unallocated {formatQty(quantity)} → 0 {unitName(plan)}</div>
        {location && <div>{location.location_name} {formatQty(location.current_qty)} → {formatQty(Number(location.current_qty) + Number(quantity))} {unitName(plan)}</div>}
        <div className="ilrExecutionNote">This correction preserves the global quantity and inventory valuation.</div>
      </div>
      <button type="button" className="ilrButton" disabled={disabled || !location}
        onClick={async () => {
          await onApply(plan, location);
          setLocationId("");
        }}>
        Apply rounding correction
      </button>
    </div>
  );
}

function cleanHierarchyLabel(value) {
  const label = String(value || "").trim();
  return label || "—";
}

export function inventoryReconciliationHierarchy(plan) {
  const item = plan?.item || {};
  const category = item.category || item.subcategory?.category || {};
  const subfamily = category.subfamily || item.subfamily || {};
  const family = subfamily.family || item.family || {};

  return {
    family: cleanHierarchyLabel(item.family_name || family.name),
    subfamily: cleanHierarchyLabel(item.subfamily_name || subfamily.name),
    category: cleanHierarchyLabel(item.category_name || category.name),
    subcategory: cleanHierarchyLabel(
      item.subcategory_name || item.subcategory?.name
    ),
  };
}

function normalizeManualSearch(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function inventoryReconciliationSearchText(plan) {
  const hierarchy = inventoryReconciliationHierarchy(plan);
  return normalizeManualSearch(
    [
      hierarchy.family,
      hierarchy.subfamily,
      hierarchy.category,
      hierarchy.subcategory,
      plan?.item?.code,
      plan?.item?.name,
      unitName(plan),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

export function hasLegacyUnallocatedBalance(plan) {
  const quantity = Number(plan?.legacy_unallocated?.current_qty || 0);
  return Number.isFinite(quantity) && Math.abs(quantity) >= 0.00000001;
}

function editableQty(value) {
  const quantity = Number(value || 0);
  if (!Number.isFinite(quantity)) return "0";
  return quantity.toFixed(8).replace(/\.?0+$/, "") || "0";
}

export function defaultManualTargets(plan) {
  return {
    ...Object.fromEntries(
      (plan?.locations || []).map((location) => [
        String(location.location_id),
        editableQty(location.is_active === false ? 0 : location.current_qty),
      ])
    ),
    [LEGACY_TARGET_KEY]: editableQty(plan?.legacy_unallocated?.current_qty || 0),
  };
}

function readManualQuantity(rawValue) {
  const text = String(rawValue ?? "").trim();
  if (!text) return { valid: false, quantity: null };
  if (!/^\d+(?:\.\d{0,8})?$/.test(text)) return { valid: false, quantity: null };
  const quantity = Number(text);
  return Number.isFinite(quantity) && quantity >= 0 && quantity < 1_000_000_000_000
    ? { valid: true, quantity }
    : { valid: false, quantity: null };
}

export function manualImpact(plan, storedDraft) {
  const draft = storedDraft || defaultManualTargets(plan);
  let valid = true;
  const targetByLocationId = {};
  let targetGlobalQty = 0;

  (plan?.locations || []).forEach((location) => {
    const locationId = String(location.location_id);
    const parsed = readManualQuantity(draft[locationId]);
    if (!parsed.valid || (location.is_active === false && parsed.quantity > 0)) {
      valid = false;
      targetByLocationId[locationId] = null;
      return;
    }
    targetByLocationId[locationId] = parsed.quantity;
    targetGlobalQty += parsed.quantity;
  });

  const parsedLegacy = readManualQuantity(draft[LEGACY_TARGET_KEY]);
  if (!parsedLegacy.valid) {
    valid = false;
  } else {
    targetGlobalQty += parsedLegacy.quantity;
  }

  targetGlobalQty = Number(targetGlobalQty.toFixed(8));
  if (!Number.isFinite(targetGlobalQty) || targetGlobalQty >= 1_000_000_000_000) {
    valid = false;
  }
  return {
    draft,
    valid,
    targetByLocationId,
    legacyUnallocatedTarget: parsedLegacy.valid ? parsedLegacy.quantity : null,
    targetGlobalQty,
    globalAdjustmentQty: valid
      ? Number((targetGlobalQty - Number(plan?.current_global_qty || 0)).toFixed(8))
      : null,
  };
}

export function buildManualResetItemPayload(plan, storedDraft) {
  const impact = manualImpact(plan, storedDraft);
  return {
    item_id: Number(plan.item_id),
    fingerprint: plan.fingerprint,
    location_targets: (plan.locations || []).map((location) => ({
      location_id: Number(location.location_id),
      quantity: impact.targetByLocationId[String(location.location_id)],
    })),
    legacy_unallocated_target: impact.legacyUnallocatedTarget,
  };
}

const ManualInventoryGridRow = React.memo(function ManualInventoryGridRow({
  applying,
  draft,
  gridLocations,
  onTargetChange,
  onToggle,
  plan,
  selected,
}) {
  const hierarchy = inventoryReconciliationHierarchy(plan);
  const impact = manualImpact(plan, draft);
  const planLocations = new Map(
    (plan.locations || []).map((location) => [
      Number(location.location_id),
      location,
    ])
  );
  const rowUnavailable = !plan.manual_reset_available;
  const rowTitle = rowUnavailable
    ? plan.manual_reset_unavailable_reason || "Manual reset unavailable"
    : selected
      ? impact.valid
        ? `Selected · global ${formatQty(plan.current_global_qty)} → ${formatQty(impact.targetGlobalQty)}`
        : "Selected · one or more targets are invalid"
      : "Edit any quantity to select this row";

  return (
    <tr
      className={`${selected ? "ilrManualRow--selected" : ""} ${rowUnavailable ? "ilrManualRow--unavailable" : ""}`.trim()}
      title={rowTitle}
    >
      <td className="ilrManualItemCell">
        <div className="ilrManualItemMain">
          <input
            className="ilrCheck"
            type="checkbox"
            checked={selected}
            disabled={rowUnavailable || applying}
            aria-label={`Select ${plan.item?.name || plan.item_id}`}
            onChange={() => onToggle(plan.item_id)}
          />
          <div className="ilrManualItemText">
            <div className="ilrManualItemName" title={plan.item?.name || ""}>
              {plan.item?.name || `Item ${plan.item_id}`}
            </div>
            <div className="ilrManualItemMeta">
              {plan.item?.code || "No code"} · {unitName(plan)} · global {formatQty(plan.current_global_qty)} → {impact.valid ? formatQty(impact.targetGlobalQty) : "invalid"}
            </div>
            <div
              className="ilrManualHierarchy"
              title={`${hierarchy.family} › ${hierarchy.subfamily} › ${hierarchy.category} › ${hierarchy.subcategory}`}
            >
              {hierarchy.family} › {hierarchy.subfamily} › {hierarchy.category} › {hierarchy.subcategory}
            </div>
          </div>
        </div>
      </td>
      {gridLocations.map((gridLocation) => {
        const location = planLocations.get(Number(gridLocation.location_id));
        if (!location) {
          return <td className="ilrManualQtyCell" key={gridLocation.location_id}>—</td>;
        }
        const locationKey = String(location.location_id);
        const rawValue = impact.draft[locationKey] ?? "";
        const parsed = readManualQuantity(rawValue);
        const changed =
          parsed.valid &&
          Math.abs(parsed.quantity - Number(location.current_qty || 0)) >= 0.00000001;
        const inputClass = [
          "ilrManualQtyInput",
          !parsed.valid ? "ilrManualQtyInput--invalid" : "",
          changed ? "ilrManualQtyInput--changed" : "",
        ].filter(Boolean).join(" ");
        return (
          <td className="ilrManualQtyCell" key={location.location_id}>
            <input
              className={inputClass}
              type="text"
              inputMode="decimal"
              value={rawValue}
              disabled={applying || rowUnavailable || location.is_active === false}
              aria-label={`${plan.item?.name || plan.item_id} — ${location.location_name} final quantity`}
              title={
                location.is_active === false
                  ? `Current: ${formatQty(location.current_qty)} ${unitName(plan)} · inactive; final quantity fixed at 0`
                  : `Current: ${formatQty(location.current_qty)} ${unitName(plan)}`
              }
              onChange={(event) =>
                onTargetChange(plan, location.location_id, event.target.value)
              }
            />
          </td>
        );
      })}
      <td className="ilrManualQtyCell ilrManualLegacyCell">
        {(() => {
          const rawValue = impact.draft[LEGACY_TARGET_KEY] ?? "";
          const parsed = readManualQuantity(rawValue);
          const currentLegacy = Number(plan.legacy_unallocated?.current_qty || 0);
          const changed =
            parsed.valid && Math.abs(parsed.quantity - currentLegacy) >= 0.00000001;
          const inputClass = [
            "ilrManualQtyInput",
            !parsed.valid ? "ilrManualQtyInput--invalid" : "",
            changed ? "ilrManualQtyInput--changed" : "",
          ].filter(Boolean).join(" ");
          return (
            <input
              className={inputClass}
              type="text"
              inputMode="decimal"
              value={rawValue}
              disabled={applying || rowUnavailable}
              aria-label={`${plan.item?.name || plan.item_id} — Legacy / Unallocated final quantity`}
              title={`Current: ${formatQty(currentLegacy)} ${unitName(plan)}`}
              onChange={(event) =>
                onTargetChange(plan, LEGACY_TARGET_KEY, event.target.value)
              }
            />
          );
        })()}
      </td>
    </tr>
  );
});

function InventoryLocationReconciliationAdmin() {
  const isMasterAdmin = useMemo(() => Number(readStoredUser()?.id) === 1, []);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [businessUnitId, setBusinessUnitId] = useState("");
  const [plans, setPlans] = useState([]);
  const [summary, setSummary] = useState({
    scanned: 0,
    discrepancies: 0,
    ready: 0,
    blocked: 0,
    inconclusive: 0,
  });
  const [offset, setOffset] = useState(0);
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);
  const [serverHasMore, setServerHasMore] = useState(false);
  const [totalDiscrepancies, setTotalDiscrepancies] = useState(0);
  const [hasPreview, setHasPreview] = useState(false);
  const [selectedPlansById, setSelectedPlansById] = useState({});
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loadingBusinessUnits, setLoadingBusinessUnits] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [applyResults, setApplyResults] = useState([]);
  const [executionMode, setExecutionMode] = useState("safe_reconciliation");
  const [manualTargetsByItem, setManualTargetsByItem] = useState({});
  const [manualSearch, setManualSearch] = useState("");
  const [manualFamilyFilter, setManualFamilyFilter] = useState("all");
  const [manualSubfamilyFilter, setManualSubfamilyFilter] = useState("all");
  const [manualCategoryFilter, setManualCategoryFilter] = useState("all");
  const [manualSubcategoryFilter, setManualSubcategoryFilter] = useState("all");
  const [manualAvailabilityFilter, setManualAvailabilityFilter] = useState("all");

  useEffect(() => {
    if (!isMasterAdmin) {
      setLoadingBusinessUnits(false);
      return undefined;
    }

    let active = true;

    async function loadBusinessUnits() {
      setLoadingBusinessUnits(true);
      try {
        const response = await axios.get(
          "/api/admin/inventory-location-reconciliation/business-units"
        );
        if (!active) return;
        const rows = Array.isArray(response?.data?.business_units)
          ? response.data.business_units
          : [];
        setBusinessUnits(rows);
        setBusinessUnitId((current) => current || (rows[0]?.id ? String(rows[0].id) : ""));
      } catch (requestError) {
        if (!active) return;
        setError(
          requestError?.response?.data?.error ||
            "Could not load business units. Master-admin access is required."
        );
      } finally {
        if (active) setLoadingBusinessUnits(false);
      }
    }

    loadBusinessUnits();
    return () => {
      active = false;
    };
  }, [isMasterAdmin]);

  const loadPreview = useCallback(
    async ({
      preserveNotice = false,
      preserveResults = false,
      preserveSelection = false,
      requestedOffset = 0,
      requestedLimit =
        executionMode === "manual_location_reset" ? MANUAL_GRID_LIMIT : PAGE_SIZE,
      requestedExecutionMode = executionMode,
      requestedIncludeAll = requestedExecutionMode === "manual_location_reset",
    } = {}) => {
      if (!businessUnitId) return;
      setLoadingPreview(true);
      setError("");
      if (!preserveNotice) setNotice("");
      if (!preserveResults) setApplyResults([]);
      setConfirmation("");

      try {
        const response = await axios.get(
          "/api/admin/inventory-location-reconciliation/preview",
          {
            params: buildInventoryReconciliationPreviewParams({
              businessUnitId,
              offset: requestedOffset,
              limit: requestedLimit,
              includeAll: requestedIncludeAll,
            }),
          }
        );
        const payload = response?.data || {};
        const serverPlans = Array.isArray(payload.plans) ? payload.plans : [];
        const loadedPlans = serverPlans.map(blockUnsafeMainProposal);
        const blockedHere = loadedPlans.filter((plan, index) => plan !== serverPlans[index]).length;
        setPlans(loadedPlans);
        setSummary({ ...payload.summary,
          ready: Math.max(0, Number(payload.summary?.ready || 0) - blockedHere),
          blocked: Number(payload.summary?.blocked || 0) + blockedHere,
        });
        const loadedOffset = Math.max(Number(payload.offset) || 0, 0);
        const loadedLimit = Math.max(Number(payload.limit) || Number(requestedLimit) || 1, 1);
        setOffset(loadedOffset);
        setPageLimit(loadedLimit);
        setServerHasMore(Boolean(payload.has_more));
        setTotalDiscrepancies(Math.max(Number(payload.total) || 0, 0));
        setHasPreview(true);
        setSelectedPlansById((current) =>
          mergeReconciliationPageSelections({
            selectedPlansById: current,
            pagePlans: loadedPlans,
            executionMode: requestedExecutionMode,
            preserveSelection,
            autoSelectReady: true,
            pageOffset: loadedOffset,
          })
        );
        if (!preserveSelection) setManualTargetsByItem({});
        return { plans: loadedPlans, offset: loadedOffset, total: Number(payload.total) || 0 };
      } catch (requestError) {
        setPlans([]);
        setPageLimit(requestedLimit);
        setServerHasMore(false);
        setTotalDiscrepancies(0);
        setHasPreview(false);
        setError(
          requestError?.response?.data?.error || "Could not build the reconciliation preview."
        );
        return [];
      } finally {
        setLoadingPreview(false);
      }
    },
    [businessUnitId, executionMode]
  );

  const pagePlans = plans;
  const eligiblePagePlans = useMemo(
    () =>
      pagePlans.filter((plan) =>
        executionMode === "manual_location_reset"
          ? Boolean(plan.manual_reset_available)
          : isSafeBatchPlan(plan)
      ),
    [executionMode, pagePlans]
  );
  const selectedPlans = useMemo(
    () =>
      Object.values(selectedPlansById).filter(
        (plan) =>
          (executionMode === "manual_location_reset"
            ? Boolean(plan.manual_reset_available)
            : isSafeBatchPlan(plan))
      ),
    [executionMode, selectedPlansById]
  );
  const selectedIds = useMemo(
    () => new Set(selectedPlans.map((plan) => Number(plan.item_id))),
    [selectedPlans]
  );
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const manualGridLocations = useMemo(() => {
    const byId = new Map();
    plans.forEach((plan) => {
      (plan.locations || []).forEach((location) => {
        const locationId = Number(location.location_id);
        if (!locationId || byId.has(locationId)) return;
        byId.set(locationId, location);
      });
    });
    return Array.from(byId.values()).sort((left, right) => {
      const leftOrder = Number(left.sort_order || 0);
      const rightOrder = Number(right.sort_order || 0);
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      if (Boolean(left.is_main) !== Boolean(right.is_main)) return left.is_main ? -1 : 1;
      return String(left.location_name || "").localeCompare(String(right.location_name || ""));
    });
  }, [plans]);
  const manualFamilyOptions = useMemo(
    () =>
      Array.from(
        new Set(plans.map((plan) => inventoryReconciliationHierarchy(plan).family))
      ).sort((left, right) => left.localeCompare(right)),
    [plans]
  );
  const manualSubfamilyOptions = useMemo(
    () =>
      Array.from(
        new Set(plans.map((plan) => inventoryReconciliationHierarchy(plan).subfamily))
      ).sort((left, right) => left.localeCompare(right)),
    [plans]
  );
  const manualCategoryOptions = useMemo(
    () =>
      Array.from(
        new Set(plans.map((plan) => inventoryReconciliationHierarchy(plan).category))
      ).sort((left, right) => left.localeCompare(right)),
    [plans]
  );
  const manualSubcategoryOptions = useMemo(
    () =>
      Array.from(
        new Set(plans.map((plan) => inventoryReconciliationHierarchy(plan).subcategory))
      ).sort((left, right) => left.localeCompare(right)),
    [plans]
  );
  const manualFilteredPlans = useMemo(() => {
    const query = normalizeManualSearch(manualSearch);
    return plans.filter((plan) => {
      const hierarchy = inventoryReconciliationHierarchy(plan);
      if (manualFamilyFilter !== "all" && hierarchy.family !== manualFamilyFilter) return false;
      if (manualSubfamilyFilter !== "all" && hierarchy.subfamily !== manualSubfamilyFilter) return false;
      if (manualCategoryFilter !== "all" && hierarchy.category !== manualCategoryFilter) return false;
      if (manualSubcategoryFilter !== "all" && hierarchy.subcategory !== manualSubcategoryFilter) return false;
      if (manualAvailabilityFilter === "available" && !plan.manual_reset_available) return false;
      if (manualAvailabilityFilter === "unavailable" && plan.manual_reset_available) return false;
      if (manualAvailabilityFilter === "legacy" && !hasLegacyUnallocatedBalance(plan)) return false;
      if (manualAvailabilityFilter === "selected" && !selectedIds.has(Number(plan.item_id))) return false;
      return !query || inventoryReconciliationSearchText(plan).includes(query);
    });
  }, [
    manualAvailabilityFilter,
    manualCategoryFilter,
    manualFamilyFilter,
    manualSearch,
    manualSubcategoryFilter,
    manualSubfamilyFilter,
    plans,
    selectedIds,
  ]);
  const allEligibleSelected =
    eligiblePagePlans.length > 0 &&
    eligiblePagePlans.every((plan) => selectedIds.has(Number(plan.item_id)));
  const manualSelectionsValid =
    executionMode !== "manual_location_reset" ||
    selectedPlans.every((plan) =>
      manualImpact(plan, manualTargetsByItem[String(plan.item_id)]).valid
    );
  const confirmationText = SAFE_CONFIRMATION_TEXT;
  const canApply = canApplyLoadedReconciliationPlans({
    selectedPlanCount: selectedPlans.length,
    reason,
    minimumReasonLength: executionMode === "manual_location_reset" ? 15 : 8,
    confirmation,
    confirmationText,
    manualSelectionsValid,
    manualMode: executionMode === "manual_location_reset",
    applying,
  });

  const togglePlan = useCallback((itemId) => {
    const plan = plans.find((candidate) => Number(candidate.item_id) === Number(itemId));
    const normalizedItemId = Number(itemId);
    const currentSelectedIds = selectedIdsRef.current;
    if (!currentSelectedIds.has(normalizedItemId) && currentSelectedIds.size >= 50) {
      setError("A maximum of 50 items can be reset in one execution.");
      return;
    }
    setError("");
    setConfirmation("");
    setSelectedPlansById((current) => {
      const next = { ...current };
      const key = String(normalizedItemId);
      if (Object.prototype.hasOwnProperty.call(next, key)) delete next[key];
      else if (plan) {
        next[key] = {
          ...plan,
          selection_offset:
            offset + plans.findIndex((candidate) => candidate === plan),
        };
      }
      return next;
    });
    if (executionMode === "manual_location_reset" && plan) {
      setManualTargetsByItem((current) =>
        current[String(itemId)]
          ? current
          : { ...current, [String(itemId)]: defaultManualTargets(plan) }
      );
    }
  }, [executionMode, offset, plans]);

  function toggleAllEligible(
    candidatePlans = eligiblePagePlans,
    allCandidatesSelected = allEligibleSelected
  ) {
    setConfirmation("");
    let limitReached = false;
    const nextSelectedPlans = { ...selectedPlansById };
    if (allCandidatesSelected) {
      candidatePlans.forEach(
        (plan) => delete nextSelectedPlans[String(plan.item_id)]
      );
    } else {
      candidatePlans.forEach((plan) => {
        const itemId = String(plan.item_id);
        if (Object.prototype.hasOwnProperty.call(nextSelectedPlans, itemId)) return;
        if (Object.keys(nextSelectedPlans).length >= 50) {
          limitReached = true;
          return;
        }
        nextSelectedPlans[itemId] = {
          ...plan,
          selection_offset:
            offset + plans.findIndex((candidate) => candidate === plan),
        };
      });
    }
    const nextSelectedIds = new Set(Object.keys(nextSelectedPlans).map(Number));
    setSelectedPlansById(nextSelectedPlans);
    setError(
      limitReached ? "A maximum of 50 items can be reset in one execution." : ""
    );
    if (executionMode === "manual_location_reset") {
      setManualTargetsByItem((current) => {
        const next = { ...current };
        candidatePlans.forEach((plan) => {
          if (
            nextSelectedIds.has(Number(plan.item_id)) &&
            !next[String(plan.item_id)]
          ) {
            next[String(plan.item_id)] = defaultManualTargets(plan);
          }
        });
        return next;
      });
    }
  }

  function goToPage(nextOffset) {
    setConfirmation("");
    loadPreview({
      preserveSelection: true,
      requestedOffset: Math.max(0, nextOffset),
      requestedLimit: pageLimit,
      requestedExecutionMode: executionMode,
      requestedIncludeAll: executionMode === "manual_location_reset",
    });
  }

  function changeExecutionMode(nextMode) {
    if (nextMode === executionMode || loadingPreview || applying) return;
    setExecutionMode(nextMode);
    setPlans([]);
    setServerHasMore(false);
    setSelectedPlansById({});
    setManualTargetsByItem({});
    setManualSearch("");
    setManualFamilyFilter("all");
    setManualSubfamilyFilter("all");
    setManualCategoryFilter("all");
    setManualSubcategoryFilter("all");
    setManualAvailabilityFilter("all");
    setConfirmation("");
    setApplyResults([]);
    setError("");
    setNotice("");
    setOffset(0);
    const nextLimit =
      nextMode === "manual_location_reset" ? MANUAL_GRID_LIMIT : PAGE_SIZE;
    setPageLimit(nextLimit);
    if (hasPreview && businessUnitId) {
      loadPreview({
        preserveSelection: false,
        requestedOffset: 0,
        requestedLimit: nextLimit,
        requestedExecutionMode: nextMode,
        requestedIncludeAll: nextMode === "manual_location_reset",
      });
    }
  }

  const updateManualTarget = useCallback((plan, locationId, value) => {
    if (!plan?.manual_reset_available) return;
    const normalizedItemId = Number(plan.item_id);
    const currentSelectedIds = selectedIdsRef.current;
    if (!currentSelectedIds.has(normalizedItemId)) {
      if (currentSelectedIds.size >= 50) {
        setError("A maximum of 50 items can be reset in one execution.");
        return;
      }
      setSelectedPlansById((current) => ({
        ...current,
        [String(normalizedItemId)]: {
          ...plan,
          selection_offset:
            offset + plans.findIndex((candidate) => candidate === plan),
        },
      }));
    }
    setError("");
    setConfirmation("");
    setManualTargetsByItem((current) => ({
      ...current,
      [String(plan.item_id)]: {
        ...(current[String(plan.item_id)] || defaultManualTargets(plan)),
        [String(locationId)]: value,
      },
    }));
  }, [offset, plans]);

  async function applyRoundingCorrection(plan, location) {
    if (applying || loadingPreview) return;
    setApplying(true);
    setError("");
    setNotice("");
    setApplyResults([]);
    try {
      await axios.post("/api/admin/inventory-location-reconciliation/rounding-correction", {
        business_unit_id: Number(businessUnitId),
        item_id: Number(plan.item_id),
        fingerprint: plan.fingerprint,
        location_id: Number(location.location_id),
      });
      setNotice(`Rounding corrected for ${plan.item?.code || plan.item_id} at ${location.location_name}.`);
      await loadPreview({ preserveNotice: true, preserveSelection: true });
    } catch (requestError) {
      const message = requestError?.response?.data?.error || "Could not apply the rounding correction.";
      if (requestError?.response?.status === 409) {
        const refreshed = await loadPreview({ requestedOffset: offset, preserveSelection: true });
        if (refreshed?.plans) setError(`${message} Review the refreshed quantities.`);
      } else {
        setError(message);
      }
    } finally {
      setApplying(false);
    }
  }

  async function applySelected() {
    if (!canApply) return;
    setApplying(true);
    setError("");
    setNotice("");
    setApplyResults([]);

    try {
      const response = await axios.post(
        "/api/admin/inventory-location-reconciliation/apply",
        {
          business_unit_id: Number(businessUnitId),
          mode: executionMode,
          ...(executionMode === "safe_reconciliation"
            ? {
                reason: reason.trim(),
                confirmation,
              }
            : {}),
          items: selectedPlans.map((plan) => ({
            ...(executionMode === "manual_location_reset"
              ? buildManualResetItemPayload(
                  plan,
                  manualTargetsByItem[String(plan.item_id)]
                )
              : {
                  item_id: Number(plan.item_id),
                  fingerprint: plan.fingerprint,
                }),
          })),
        }
      );
      const payload = response?.data || {};
      const results = Array.isArray(payload.results) ? payload.results : [];
      const successfulItemIds = new Set(
        results.filter((result) => result.ok).map((result) => Number(result.item_id))
      );
      const removedBeforeCurrentPage = selectedPlans.filter(
        (plan) =>
          Number(plan.selection_offset) < offset &&
          successfulItemIds.has(Number(plan.item_id))
      ).length;
      const refreshOffset = Math.max(0, offset - removedBeforeCurrentPage);
      setApplyResults(results);
      setNotice(
        `${Number(payload.applied || 0)} item(s) ${executionMode === "manual_location_reset" ? "reset" : "reconciled"}; ${Number(payload.failed || 0)} failed.`
      );
      setConfirmation("");
      setSelectedPlansById({});
      setManualTargetsByItem({});
      const refreshedPage = await loadPreview({
        preserveNotice: true,
        preserveResults: true,
        preserveSelection: false,
        requestedOffset: refreshOffset,
        requestedLimit: pageLimit,
        requestedExecutionMode: executionMode,
        requestedIncludeAll: executionMode === "manual_location_reset",
      });
      if (
        !refreshedPage?.plans?.length &&
        refreshedPage?.total > 0 &&
        refreshOffset > 0
      ) {
        await loadPreview({
          preserveNotice: true,
          preserveResults: true,
          preserveSelection: false,
          requestedOffset:
            Math.floor((Number(refreshedPage.total) - 1) / pageLimit) * pageLimit,
          requestedLimit: pageLimit,
          requestedExecutionMode: executionMode,
          requestedIncludeAll: executionMode === "manual_location_reset",
        });
      }
      setApplyResults(results);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "Reconciliation could not be applied.");
    } finally {
      setApplying(false);
    }
  }

  const pageNumber = Math.floor(offset / pageLimit) + 1;
  const totalPages = Math.max(Math.ceil(totalDiscrepancies / pageLimit), 1);

  if (!isMasterAdmin) {
    return (
      <div className="alert alert-danger" role="alert">
        Master-admin access is required for inventory location reconciliation.
      </div>
    );
  }

  return (
    <div className="ilrPage">
      <style>{`
        .ilrPage { color: #eef1ff; padding: 2px; }
        .ilrHeader { display: flex; justify-content: space-between; gap: 18px; align-items: flex-start; margin-bottom: 18px; }
        .ilrTitle { margin: 0; font-size: clamp(1.35rem, 2vw, 1.85rem); font-weight: 900; letter-spacing: -0.025em; }
        .ilrSubtitle { margin: 7px 0 0; color: rgba(230,230,247,.68); max-width: 860px; line-height: 1.45; }
        .ilrSafety { flex: 0 0 auto; border: 1px solid rgba(251,191,36,.35); background: rgba(251,191,36,.08); color: #fde68a; border-radius: 999px; padding: 8px 12px; font-size: .76rem; font-weight: 850; letter-spacing: .05em; text-transform: uppercase; }
        .ilrPanel { border: 1px solid rgba(255,255,255,.1); border-radius: 17px; background: rgba(15,17,34,.82); box-shadow: 0 18px 50px rgba(0,0,0,.25); }
        .ilrFilters { display: grid; grid-template-columns: minmax(260px, 1fr) auto; gap: 12px; padding: 16px; align-items: end; }
        .ilrField { min-width: 0; }
        .ilrLabel { display: block; margin-bottom: 6px; color: rgba(230,230,247,.62); font-size: .72rem; font-weight: 850; letter-spacing: .06em; text-transform: uppercase; }
        .ilrInput, .ilrSelect, .ilrTextarea { width: 100%; color: #f5f7ff; background: rgba(255,255,255,.055); border: 1px solid rgba(255,255,255,.14); border-radius: 11px; outline: none; }
        .ilrInput, .ilrSelect { min-height: 42px; padding: 9px 11px; }
        .ilrTextarea { min-height: 82px; resize: vertical; padding: 10px 11px; }
        .ilrInput:focus, .ilrSelect:focus, .ilrTextarea:focus { border-color: rgba(48,216,255,.7); box-shadow: 0 0 0 3px rgba(48,216,255,.09); }
        .ilrButton { min-height: 42px; border: 1px solid rgba(48,216,255,.48); border-radius: 11px; padding: 9px 15px; background: linear-gradient(135deg, rgba(48,216,255,.95), rgba(56,139,253,.95)); color: #06111c; font-weight: 900; cursor: pointer; }
        .ilrButton:disabled { cursor: not-allowed; opacity: .42; }
        .ilrButton--secondary { color: #eafcff; background: rgba(48,216,255,.08); }
        .ilrButton--danger { color: #fff7ed; border-color: rgba(249,115,22,.65); background: linear-gradient(135deg, #f59e0b, #ea580c); }
        .ilrButton--manual { color: #fff7ed; border-color: rgba(248,113,113,.72); background: linear-gradient(135deg, #dc2626, #991b1b); }
        .ilrMessage { margin-top: 14px; padding: 11px 13px; border-radius: 12px; border: 1px solid; font-size: .87rem; }
        .ilrMessage--error { border-color: rgba(248,113,113,.45); background: rgba(127,29,29,.22); color: #fecaca; }
        .ilrMessage--warning { border-color: rgba(251,191,36,.35); background: rgba(120,53,15,.14); color: #fde68a; }
        .ilrMessage--success { border-color: rgba(52,211,153,.38); background: rgba(6,78,59,.25); color: #bbf7d0; }
        .ilrMessage--withAction { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .ilrMessageAction { flex: 0 0 auto; border: 1px solid rgba(251,191,36,.38); border-radius: 9px; padding: 6px 10px; color: #fff7d6; background: rgba(251,191,36,.09); font: inherit; font-weight: 850; cursor: pointer; }
        .ilrMessageAction:disabled { cursor: not-allowed; opacity: .45; }
        .ilrSummary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin: 14px 0; }
        .ilrSummaryCard { padding: 14px 16px; }
        .ilrSummaryValue { font-size: 1.65rem; font-weight: 950; line-height: 1; }
        .ilrSummaryLabel { margin-top: 6px; color: rgba(230,230,247,.62); font-size: .76rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; }
        .ilrToolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin: 15px 0 10px; }
        .ilrToolbarText { color: rgba(230,230,247,.66); font-size: .84rem; }
        .ilrPlans { display: grid; gap: 12px; }
        .ilrPlan { overflow: hidden; }
        .ilrPlanTop { display: grid; grid-template-columns: auto minmax(220px, 1fr) repeat(3, minmax(110px, .33fr)) auto; gap: 14px; align-items: center; padding: 14px 16px; }
        .ilrCheck { width: 18px; height: 18px; accent-color: #22d3ee; }
        .ilrItem { min-width: 0; }
        .ilrItemName { font-weight: 900; overflow-wrap: anywhere; }
        .ilrItemMeta { margin-top: 3px; color: rgba(230,230,247,.56); font-size: .76rem; }
        .ilrMetricLabel { color: rgba(230,230,247,.52); font-size: .65rem; font-weight: 850; text-transform: uppercase; letter-spacing: .05em; }
        .ilrMetricValue { margin-top: 3px; font-weight: 900; font-variant-numeric: tabular-nums; }
        .ilrAdjustment--negative { color: #fca5a5; }
        .ilrAdjustment--positive { color: #86efac; }
        .ilrBadge { justify-self: end; border-radius: 999px; padding: 7px 10px; border: 1px solid; font-size: .68rem; font-weight: 900; white-space: nowrap; text-transform: uppercase; letter-spacing: .045em; }
        .ilrBadge--ready { color: #a5f3fc; border-color: rgba(34,211,238,.42); background: rgba(8,145,178,.15); }
        .ilrBadge--blocked { color: #fecaca; border-color: rgba(248,113,113,.38); background: rgba(127,29,29,.18); }
        .ilrBadge--reconciled { color: #bbf7d0; border-color: rgba(74,222,128,.35); background: rgba(22,101,52,.18); }
        .ilrLocations { border-top: 1px solid rgba(255,255,255,.075); background: rgba(0,0,0,.11); }
        .ilrRounding { display: flex; flex-wrap: wrap; align-items: center; gap: 16px; padding: 14px 16px; border-top: 1px solid rgba(255,255,255,.075); }
        .ilrLocation { display: grid; grid-template-columns: minmax(170px, 1fr) repeat(3, minmax(105px, .48fr)) minmax(240px, 1.4fr); gap: 12px; padding: 10px 16px; align-items: center; border-top: 1px solid rgba(255,255,255,.055); font-size: .8rem; }
        .ilrLocation:first-child { border-top: 0; }
        .ilrLocationName { font-weight: 850; }
        .ilrFormula { color: rgba(230,230,247,.66); line-height: 1.35; overflow-wrap: anywhere; }
        .ilrEvents { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 6px; }
        .ilrEvent { border: 1px solid rgba(48,216,255,.16); border-radius: 7px; padding: 3px 6px; background: rgba(48,216,255,.055); color: rgba(220,248,255,.78); font-size: .7rem; }
        .ilrBlockers { margin: 0; padding: 11px 16px 13px 38px; border-top: 1px solid rgba(248,113,113,.16); color: #fecaca; background: rgba(127,29,29,.1); font-size: .8rem; }
        .ilrBlockers li + li { margin-top: 4px; }
        .ilrWarnings { margin: 0; padding: 11px 16px 13px 38px; border-top: 1px solid rgba(251,191,36,.14); color: #fde68a; background: rgba(120,53,15,.08); font-size: .8rem; }
        .ilrWarnings li + li { margin-top: 4px; }
        .ilrEmpty { padding: 34px 20px; text-align: center; color: rgba(230,230,247,.58); }
        .ilrPager { display: flex; justify-content: flex-end; align-items: center; gap: 9px; margin-top: 12px; }
        .ilrExecution { margin-top: 18px; padding: 17px; border-color: rgba(249,115,22,.3); }
        .ilrModeSwitch { display: inline-flex; gap: 4px; padding: 4px; margin-bottom: 15px; border: 1px solid rgba(255,255,255,.1); border-radius: 12px; background: rgba(0,0,0,.16); }
        .ilrModeButton { border: 0; border-radius: 8px; padding: 8px 11px; color: rgba(230,230,247,.62); background: transparent; font-size: .76rem; font-weight: 850; cursor: pointer; }
        .ilrModeButton--active { color: #ecfeff; background: rgba(34,211,238,.14); box-shadow: inset 0 0 0 1px rgba(34,211,238,.25); }
        .ilrModeButton--manual.ilrModeButton--active { color: #fee2e2; background: rgba(220,38,38,.2); box-shadow: inset 0 0 0 1px rgba(248,113,113,.34); }
        .ilrManualWarning { margin: 0 0 14px; padding: 11px 13px; border: 1px solid rgba(248,113,113,.34); border-radius: 11px; background: rgba(127,29,29,.14); color: #fecaca; font-size: .8rem; line-height: 1.45; }
        .ilrTargetInput { width: 100%; min-width: 80px; max-width: 132px; padding: 7px 8px; border: 1px solid rgba(248,113,113,.36); border-radius: 8px; color: #fff; background: rgba(127,29,29,.12); font-weight: 850; font-variant-numeric: tabular-nums; outline: none; }
        .ilrTargetInput:focus { border-color: rgba(248,113,113,.78); box-shadow: 0 0 0 3px rgba(248,113,113,.1); }
        .ilrManualFilters { display: grid; grid-template-columns: minmax(250px, 1.4fr) repeat(5, minmax(128px, .6fr)); gap: 9px; margin: 10px 0; }
        .ilrManualFilter { min-width: 0; }
        .ilrManualFilter .ilrInput, .ilrManualFilter .ilrSelect { min-height: 36px; padding: 6px 9px; border-radius: 8px; font-size: .78rem; }
        .ilrManualGridMeta { display: flex; justify-content: space-between; gap: 12px; align-items: center; margin: 8px 1px; color: rgba(230,230,247,.62); font-size: .75rem; }
        .ilrManualGridWrap { max-height: min(64vh, 720px); overflow: auto; border: 1px solid rgba(255,255,255,.12); border-radius: 12px; background: rgba(8,10,22,.82); scrollbar-width: thin; }
        .ilrManualGrid { width: max-content; min-width: 100%; border-collapse: separate; border-spacing: 0; font-size: .72rem; font-variant-numeric: tabular-nums; }
        .ilrManualGrid th, .ilrManualGrid td { height: 38px; padding: 5px 7px; border-right: 1px solid rgba(255,255,255,.075); border-bottom: 1px solid rgba(255,255,255,.075); white-space: nowrap; vertical-align: middle; }
        .ilrManualGrid th { position: sticky; top: 0; z-index: 4; height: 40px; color: rgba(235,242,255,.82); background: #171b2e; font-size: .65rem; font-weight: 900; letter-spacing: .045em; text-align: left; text-transform: uppercase; }
        .ilrManualGrid tr:last-child td { border-bottom: 0; }
        .ilrManualGrid th:last-child, .ilrManualGrid td:last-child { border-right: 0; }
        .ilrManualGrid tbody tr { background: rgba(255,255,255,.015); }
        .ilrManualGrid tbody tr:nth-child(even) { background: rgba(255,255,255,.027); }
        .ilrManualGrid tbody tr:hover { background: rgba(34,211,238,.06); }
        .ilrManualGrid tbody tr.ilrManualRow--selected { background: rgba(34,211,238,.075); }
        .ilrManualGrid tbody tr.ilrManualRow--unavailable { opacity: .58; }
        .ilrManualItemCell { position: sticky !important; left: 0; z-index: 2; width: 350px; min-width: 350px; max-width: 350px; background: #111527; }
        .ilrManualGrid th.ilrManualItemCell { z-index: 6; background: #171b2e; }
        .ilrManualRow--selected .ilrManualItemCell { background: #142434; }
        .ilrManualItemHeader, .ilrManualItemMain { display: flex; align-items: center; gap: 8px; min-width: 0; }
        .ilrManualItemHeader { font-size: .68rem; }
        .ilrManualItemText { min-width: 0; overflow: hidden; }
        .ilrManualItemName { overflow: hidden; color: #f8fafc; font-size: .76rem; font-weight: 900; text-overflow: ellipsis; }
        .ilrManualItemMeta { margin-top: 2px; overflow: hidden; color: #a5f3fc; font-size: .64rem; font-weight: 750; text-overflow: ellipsis; }
        .ilrManualHierarchy { margin-top: 2px; overflow: hidden; color: rgba(230,230,247,.52); font-size: .62rem; text-overflow: ellipsis; }
        .ilrManualQtyHeader { width: 128px; min-width: 128px; text-align: right !important; overflow: hidden; text-overflow: ellipsis; }
        .ilrManualQtyHeader--inactive { color: rgba(230,230,247,.42) !important; background: #151728 !important; }
        .ilrManualInactiveLabel { display: block; margin-top: 2px; font-size: .55rem; letter-spacing: .03em; }
        .ilrManualQtyCell { width: 128px; min-width: 128px; padding: 3px 5px !important; text-align: right; }
        .ilrManualQtyInput { width: 100%; min-width: 0; height: 29px; padding: 4px 7px; border: 1px solid rgba(148,163,184,.3); border-radius: 6px; color: #f8fafc; background: rgba(255,255,255,.045); font: inherit; font-weight: 850; text-align: right; outline: none; }
        .ilrManualQtyInput:hover:not(:disabled) { border-color: rgba(34,211,238,.52); }
        .ilrManualQtyInput:focus { border-color: #22d3ee; box-shadow: inset 0 0 0 1px #22d3ee, 0 0 0 2px rgba(34,211,238,.1); }
        .ilrManualQtyInput--changed { color: #fde68a; background: rgba(120,53,15,.2); border-color: rgba(251,191,36,.42); }
        .ilrManualQtyInput--invalid { color: #fecaca; background: rgba(127,29,29,.24); border-color: rgba(248,113,113,.7); }
        .ilrManualQtyInput:disabled { opacity: .55; cursor: not-allowed; }
        .ilrManualLegacyHeader { position: sticky !important; right: 0; z-index: 7 !important; color: #fde68a !important; background: #292013 !important; box-shadow: -7px 0 12px rgba(0,0,0,.22); }
        .ilrManualLegacyCell { position: sticky; right: 0; z-index: 2; border-left: 1px solid rgba(251,191,36,.2); background: #211b1b; box-shadow: -7px 0 12px rgba(0,0,0,.18); }
        .ilrManualRow--selected .ilrManualLegacyCell { background: #2a2720; }
        .ilrManualSummaryCell { min-width: 105px; text-align: right; font-weight: 850; }
        .ilrManualStatusCell { min-width: 130px; max-width: 190px; overflow: hidden; text-overflow: ellipsis; }
        .ilrManualEmptyRow { height: 84px !important; color: rgba(230,230,247,.58); text-align: center; }
        .ilrAppliedLocations { margin: 5px 0 0; color: rgba(230,230,247,.68); font-size: .72rem; line-height: 1.4; }
        .ilrExecutionHeader { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; margin-bottom: 14px; }
        .ilrExecutionTitle { margin: 0; font-size: 1rem; font-weight: 900; }
        .ilrExecutionNote { margin: 4px 0 0; color: rgba(230,230,247,.62); font-size: .82rem; line-height: 1.4; }
        .ilrExecutionGrid { display: grid; grid-template-columns: minmax(240px, 1fr) minmax(260px, 1fr) auto; gap: 12px; align-items: end; }
        .ilrExecutionGrid--manual { display: flex; justify-content: flex-end; }
        .ilrConfirmHint { margin-top: 5px; color: rgba(230,230,247,.52); font-size: .72rem; }
        .ilrResultList { margin: 12px 0 0; padding-left: 20px; font-size: .8rem; color: rgba(230,230,247,.78); }
        @media (max-width: 1120px) {
          .ilrPlanTop { grid-template-columns: auto minmax(220px, 1fr) repeat(2, minmax(100px, .38fr)) auto; }
          .ilrPlanTop .ilrMetric:nth-of-type(4) { display: none; }
          .ilrLocation { grid-template-columns: minmax(150px, 1fr) repeat(3, minmax(85px, .4fr)); }
          .ilrFormula { grid-column: 1 / -1; }
          .ilrExecutionGrid { grid-template-columns: 1fr 1fr; }
          .ilrExecutionGrid .ilrButton { grid-column: 1 / -1; }
          .ilrManualFilters { grid-template-columns: repeat(3, minmax(150px, 1fr)); }
        }
        @media (max-width: 760px) {
          .ilrHeader, .ilrToolbar, .ilrExecutionHeader { flex-direction: column; }
          .ilrMessage--withAction { align-items: stretch; flex-direction: column; }
          .ilrMessageAction { align-self: flex-start; }
          .ilrSafety { align-self: flex-start; }
          .ilrFilters, .ilrSummary, .ilrExecutionGrid, .ilrManualFilters { grid-template-columns: 1fr; }
          .ilrPlanTop { grid-template-columns: auto minmax(0, 1fr) auto; }
          .ilrPlanTop .ilrMetric { display: none; }
          .ilrLocation { grid-template-columns: 1fr 1fr; }
          .ilrFormula { grid-column: 1 / -1; }
          .ilrExecutionGrid .ilrButton { grid-column: auto; }
          .ilrManualItemCell { width: 240px; min-width: 240px; max-width: 240px; }
          .ilrManualQtyHeader, .ilrManualQtyCell { width: 112px; min-width: 112px; }
        }
      `}</style>

      <div className="ilrHeader">
        <div>
          <h1 className="ilrTitle">Inventory Location Reconciliation</h1>
          <p className="ilrSubtitle">
            Safe mode shows only detected discrepancies; Manual exact totals loads the complete
            item catalog. The audit runs on demand and never changes inventory by itself.
          </p>
        </div>
        <div className="ilrSafety">Master admin only</div>
      </div>

      <section className="ilrPanel ilrFilters">
        <label className="ilrField">
          <span className="ilrLabel">Business unit</span>
          <select
            className="ilrSelect"
            value={businessUnitId}
            disabled={loadingBusinessUnits || loadingPreview || applying}
            onChange={(event) => {
              setBusinessUnitId(event.target.value);
              setHasPreview(false);
              setPlans([]);
              setOffset(0);
              setPageLimit(
                executionMode === "manual_location_reset" ? MANUAL_GRID_LIMIT : PAGE_SIZE
              );
              setServerHasMore(false);
              setTotalDiscrepancies(0);
              setSelectedPlansById({});
              setManualTargetsByItem({});
              setManualSearch("");
              setManualFamilyFilter("all");
              setManualSubfamilyFilter("all");
              setManualCategoryFilter("all");
              setManualSubcategoryFilter("all");
              setManualAvailabilityFilter("all");
              setConfirmation("");
            }}
          >
            {!businessUnits.length ? <option value="">No business units</option> : null}
            {businessUnits.map((businessUnit) => (
              <option key={businessUnit.id} value={businessUnit.id}>
                {businessUnit.name}
              </option>
            ))}
          </select>
        </label>
        <button
          className="ilrButton"
          type="button"
          disabled={!businessUnitId || loadingPreview || applying}
          onClick={() => loadPreview()}
        >
          {loadingPreview ? "Auditing every item…" : "Audit entire business unit"}
        </button>
      </section>

      {error ? <div className="ilrMessage ilrMessage--error">{error}</div> : null}
      {notice ? <div className="ilrMessage ilrMessage--success">{notice}</div> : null}

      {hasPreview ? (
        <>
          <div className="ilrSummary">
            <div className="ilrPanel ilrSummaryCard">
              <div className="ilrSummaryValue">{summary.scanned || 0}</div>
              <div className="ilrSummaryLabel">Items scanned</div>
            </div>
            <div className="ilrPanel ilrSummaryCard">
              <div className="ilrSummaryValue">{summary.discrepancies || 0}</div>
              <div className="ilrSummaryLabel">Discrepancies found</div>
            </div>
            <div className="ilrPanel ilrSummaryCard">
              <div className="ilrSummaryValue">{summary.ready || 0}</div>
              <div className="ilrSummaryLabel">Safe to reconcile</div>
            </div>
            <div className="ilrPanel ilrSummaryCard">
              <div className="ilrSummaryValue">{summary.blocked || 0}</div>
              <div className="ilrSummaryLabel">Need manual review</div>
            </div>
          </div>

          {summary.inconclusive ? (
            <div className="ilrMessage ilrMessage--warning">
              {executionMode === "manual_location_reset"
                ? `${summary.inconclusive} item(s) could not be independently verified. They remain visible so an administrator can enter authoritative exact totals.`
                : `${summary.inconclusive} additional item(s) could not be independently verified, but no calculable discrepancy was found. They are not included in the safe correction list.`}
            </div>
          ) : null}
          <div className="ilrModeSwitch" aria-label="Reconciliation execution mode">
              <button
                className={`ilrModeButton ${executionMode === "safe_reconciliation" ? "ilrModeButton--active" : ""}`}
                type="button"
                disabled={loadingPreview || applying}
                onClick={() => changeExecutionMode("safe_reconciliation")}
              >
                Safe reconciliation
              </button>
              <button
                className={`ilrModeButton ilrModeButton--manual ${executionMode === "manual_location_reset" ? "ilrModeButton--active" : ""}`}
                type="button"
                disabled={loadingPreview || applying}
                onClick={() => changeExecutionMode("manual_location_reset")}
              >
                Manual exact totals
              </button>
          </div>

          {plans.length ? <div className="ilrToolbar">
            <div className="ilrToolbarText">
              Page {pageNumber} of {totalPages} · {executionMode === "manual_location_reset" ? "items" : "discrepancies"} {offset + 1}–{Math.min(offset + plans.length, totalDiscrepancies)} of {totalDiscrepancies}. {executionMode === "manual_location_reset"
                ? `${manualFilteredPlans.length} match the current filters. Editing a cell selects its row.`
                : "Ready items are selected up to the 50-item batch limit; selections remain active while paging."}
            </div>
            {executionMode === "safe_reconciliation" ? (
              <button
                className="ilrButton ilrButton--secondary"
                type="button"
                disabled={!eligiblePagePlans.length || applying}
                onClick={() => toggleAllEligible()}
              >
                {allEligibleSelected ? "Clear page selection" : "Select ready on page"}
              </button>
            ) : null}
          </div> : null}

          {executionMode === "manual_location_reset" ? (
            <section className="ilrManualWorkspace" aria-label="Manual exact inventory totals">
              <div className="ilrManualFilters">
                <label className="ilrManualFilter">
                  <span className="ilrLabel">Search items</span>
                  <input
                    className="ilrInput"
                    type="search"
                    value={manualSearch}
                    placeholder="Code, item, unit or hierarchy…"
                    onChange={(event) => setManualSearch(event.target.value)}
                  />
                </label>
                <label className="ilrManualFilter">
                  <span className="ilrLabel">Family</span>
                  <select
                    className="ilrSelect"
                    value={manualFamilyFilter}
                    onChange={(event) => setManualFamilyFilter(event.target.value)}
                  >
                    <option value="all">All families</option>
                    {manualFamilyOptions.map((family) => (
                      <option key={family} value={family}>{family}</option>
                    ))}
                  </select>
                </label>
                <label className="ilrManualFilter">
                  <span className="ilrLabel">Subfamily</span>
                  <select
                    className="ilrSelect"
                    value={manualSubfamilyFilter}
                    onChange={(event) => setManualSubfamilyFilter(event.target.value)}
                  >
                    <option value="all">All subfamilies</option>
                    {manualSubfamilyOptions.map((subfamily) => (
                      <option key={subfamily} value={subfamily}>{subfamily}</option>
                    ))}
                  </select>
                </label>
                <label className="ilrManualFilter">
                  <span className="ilrLabel">Category</span>
                  <select
                    className="ilrSelect"
                    value={manualCategoryFilter}
                    onChange={(event) => setManualCategoryFilter(event.target.value)}
                  >
                    <option value="all">All categories</option>
                    {manualCategoryOptions.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </label>
                <label className="ilrManualFilter">
                  <span className="ilrLabel">Subcategory</span>
                  <select
                    className="ilrSelect"
                    value={manualSubcategoryFilter}
                    onChange={(event) => setManualSubcategoryFilter(event.target.value)}
                  >
                    <option value="all">All subcategories</option>
                    {manualSubcategoryOptions.map((subcategory) => (
                      <option key={subcategory} value={subcategory}>{subcategory}</option>
                    ))}
                  </select>
                </label>
                <label className="ilrManualFilter">
                  <span className="ilrLabel">Rows</span>
                  <select
                    className="ilrSelect"
                    value={manualAvailabilityFilter}
                    onChange={(event) => setManualAvailabilityFilter(event.target.value)}
                  >
                    <option value="all">All items</option>
                    <option value="available">Reset available</option>
                    <option value="unavailable">Reset unavailable</option>
                    <option value="legacy">Legacy / Unallocated ≠ 0</option>
                    <option value="selected">Selected only</option>
                  </select>
                </label>
              </div>

              <div className="ilrManualGridMeta">
                <span>
                  {manualFilteredPlans.length} visible · {plans.length} loaded · {selectedPlans.length} selected
                </span>
                <span>Targets use each item's main unit. Changed cells are highlighted.</span>
              </div>

              <div className="ilrManualGridWrap">
                <table className="ilrManualGrid">
                  <thead>
                    <tr>
                      <th className="ilrManualItemCell">
                        <span className="ilrManualItemHeader">
                          <span>Item</span>
                        </span>
                      </th>
                      {manualGridLocations.map((location) => (
                        <th
                          className={`ilrManualQtyHeader ${location.is_active === false ? "ilrManualQtyHeader--inactive" : ""}`.trim()}
                          key={location.location_id}
                          title={`${location.location_name}${location.is_active === false ? " (inactive; forced to zero)" : ""}`}
                        >
                          {location.location_name}
                          {location.is_active === false ? (
                            <span className="ilrManualInactiveLabel">Inactive · fixed 0</span>
                          ) : null}
                        </th>
                      ))}
                      <th className="ilrManualQtyHeader ilrManualLegacyHeader">
                        Legacy / Unallocated
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {manualFilteredPlans.length ? (
                      manualFilteredPlans.map((plan) => (
                        <ManualInventoryGridRow
                          applying={applying}
                          draft={manualTargetsByItem[String(plan.item_id)]}
                          gridLocations={manualGridLocations}
                          key={plan.item_id}
                          onTargetChange={updateManualTarget}
                          onToggle={togglePlan}
                          plan={plan}
                          selected={selectedIds.has(Number(plan.item_id))}
                        />
                      ))
                    ) : (
                      <tr>
                        <td
                          className="ilrManualEmptyRow"
                          colSpan={manualGridLocations.length + 2}
                        >
                          {loadingPreview
                            ? "Loading inventory items…"
                            : "No items match the current manual-grid filters."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
          <div className="ilrPlans">
            {!pagePlans.length ? (
              <div className="ilrPanel ilrEmpty">
                Audit complete: no calculable inventory discrepancies were found.
              </div>
            ) : (
              pagePlans.map((plan) => {
                const isManualMode = executionMode === "manual_location_reset";
                const selected =
                  selectedIds.has(Number(plan.item_id)) &&
                  (isManualMode
                    ? Boolean(plan.manual_reset_available)
                    : isSafeBatchPlan(plan));
                const resetImpact = manualImpact(
                  plan,
                  manualTargetsByItem[String(plan.item_id)]
                );
                const legacy = plan.legacy_unallocated || null;
                const blockers = (plan.blockers || []).filter((blocker) =>
                  !plan.rounding_correction || blocker.code !== "unassigned_location_balance"
                );
                const showLegacy =
                  legacy &&
                  (Math.abs(Number(legacy.current_qty || 0)) >= 0.00000001 ||
                    Math.abs(Number(legacy.target_qty || 0)) >= 0.00000001 ||
                    (legacy.events || []).length > 0);
                const displayLocations = [
                  ...(plan.locations || []),
                  ...(showLegacy
                    ? [
                        {
                          ...legacy,
                          location_id: "legacy_unallocated",
                          is_legacy: true,
                          ...(isManualMode
                            ? {
                                target_qty: 0,
                                adjustment_qty: -Number(legacy.current_qty || 0),
                              }
                            : {}),
                        },
                      ]
                    : []),
                ];
                const adjustmentClass =
                  Number(
                    isManualMode
                      ? resetImpact.globalAdjustmentQty
                      : plan.global_adjustment_qty
                  ) < 0
                    ? "ilrAdjustment--negative"
                    : Number(
                          isManualMode
                            ? resetImpact.globalAdjustmentQty
                            : plan.global_adjustment_qty
                        ) > 0
                      ? "ilrAdjustment--positive"
                      : "";
                return (
                  <article className="ilrPanel ilrPlan" key={plan.item_id}>
                    <div className="ilrPlanTop">
                      <input
                        className="ilrCheck"
                        type="checkbox"
                        checked={selected}
                        disabled={
                          (isManualMode
                            ? !plan.manual_reset_available
                            : !isSafeBatchPlan(plan)) || applying
                        }
                        aria-label={`Select ${plan.item?.name || plan.item_id}`}
                        onChange={() => togglePlan(plan.item_id)}
                      />
                      <div className="ilrItem">
                        <div className="ilrItemName">{plan.item?.name || `Item ${plan.item_id}`}</div>
                        <div className="ilrItemMeta">
                          {plan.item?.code || "No code"} · Main unit: {unitName(plan)}
                        </div>
                      </div>
                      <div className="ilrMetric">
                        <div className="ilrMetricLabel">Current global</div>
                        <div className="ilrMetricValue">{formatQty(plan.current_global_qty)}</div>
                      </div>
                      <div className="ilrMetric">
                        <div className="ilrMetricLabel">
                          {isManualMode ? "Final global (location sum)" : "Reconstructed"}
                        </div>
                        <div className="ilrMetricValue">
                          {isManualMode
                            ? !selected
                              ? "—"
                              : resetImpact.valid
                              ? formatQty(resetImpact.targetGlobalQty)
                              : "Invalid target"
                            : formatQty(plan.target_global_qty)}
                        </div>
                      </div>
                      <div className="ilrMetric">
                        <div className="ilrMetricLabel">Global adjustment</div>
                        <div className={`ilrMetricValue ${adjustmentClass}`}>
                          {isManualMode && (!selected || !resetImpact.valid)
                            ? "—"
                            : signedQty(
                                isManualMode
                                  ? resetImpact.globalAdjustmentQty
                                  : plan.global_adjustment_qty
                              )}
                        </div>
                      </div>
                      <div className={`ilrBadge ilrBadge--${plan.status}`}>
                        {isManualMode
                          ? plan.manual_reset_available
                            ? selected
                              ? resetImpact.valid
                                ? "Ready to force"
                                : "Manual targets required"
                              : "Manual reset available"
                            : "Manual reset unavailable"
                          : plan.rounding_correction ? "Choose rounding location" : statusCopy(plan.status)}
                      </div>
                    </div>

                    {plan.rounding_correction && !isManualMode ? (
                      <RoundingCorrection key={plan.fingerprint} plan={plan}
                        disabled={applying || loadingPreview} onApply={applyRoundingCorrection} />
                    ) : (<div className="ilrLocations">
                      {displayLocations.map((location) => (
                        <div className="ilrLocation" key={location.location_id}>
                          <div>
                            <div className="ilrMetricLabel">Location</div>
                            <div className="ilrLocationName">{location.location_name}</div>
                          </div>
                          <div>
                            <div className="ilrMetricLabel">Current</div>
                            <div className="ilrMetricValue">{formatQty(location.current_qty)}</div>
                          </div>
                          <div>
                            <div className="ilrMetricLabel">Target</div>
                            <div className="ilrMetricValue">
                              {isManualMode && !location.is_legacy && selected ? (
                                <input
                                  className="ilrTargetInput"
                                  type="number"
                                  min="0"
                                  max="999999999999.99999999"
                                  step="0.00000001"
                                  inputMode="decimal"
                                  value={resetImpact.draft[String(location.location_id)] ?? ""}
                                  disabled={applying || location.is_active === false}
                                  aria-label={`${location.location_name} final quantity`}
                                  onChange={(event) =>
                                    updateManualTarget(
                                      plan,
                                      location.location_id,
                                      event.target.value
                                    )
                                  }
                                />
                              ) : isManualMode && !location.is_legacy ? (
                                resetImpact.targetByLocationId[String(location.location_id)] === null
                                  ? "—"
                                  : formatQty(
                                      resetImpact.targetByLocationId[
                                        String(location.location_id)
                                      ]
                                    )
                              ) : (
                                formatQty(location.target_qty)
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="ilrMetricLabel">Change</div>
                            <div className="ilrMetricValue">
                              {isManualMode && !location.is_legacy
                                ? resetImpact.targetByLocationId[String(location.location_id)] === null
                                  ? "—"
                                  : signedQty(
                                      Number(
                                        resetImpact.targetByLocationId[
                                          String(location.location_id)
                                        ]
                                      ) - Number(location.current_qty || 0)
                                    )
                                : signedQty(location.adjustment_qty)}
                            </div>
                          </div>
                          <div className="ilrFormula">
                            <div>
                              {isManualMode && location.is_legacy ? (
                                <>
                                  Manual reset fixes Legacy / Unallocated at 0. It is never editable.
                                </>
                              ) : isManualMode && location.is_active === false ? (
                                <>
                                  Inactive locations are fixed at 0. Move any real stock into an
                                  active location target above.
                                </>
                              ) : isManualMode ? (
                                <>
                                  Enter the exact final physical quantity for this location. Previous
                                  reconstruction is superseded.
                                </>
                              ) : location.is_legacy ? (
                                <>
                                  Historic movements without an exact location remain here; they
                                  are not deleted from global stock.
                                </>
                              ) : location.anchor ? (
                                <>
                                {location.anchor.anchor_type === "reconciliation"
                                  ? `${location.anchor.reference_label || "Location reconciliation"}: `
                                  : `Count #${location.anchor.inventory_count_id}: `}
                                {formatQty(location.anchor.selected_quantity)}
                                {location.anchor.selected_unit_name
                                  ? ` ${location.anchor.selected_unit_name}`
                                  : " selected units"}
                                {Number(location.anchor.conversion_factor) !== 1
                                  ? ` ÷ ${formatQty(location.anchor.conversion_factor)}`
                                  : ""}
                                {` + ${signedQty(location.subsequent_delta)} later = ${formatQty(location.target_qty)} ${unitName(plan)}`}
                                </>
                              ) : (
                                "No count anchor; the existing location balance is preserved."
                              )}
                            </div>
                            {location.events?.length ? (
                              <div className="ilrEvents">
                                {location.events.slice(0, 5).map((event, eventIndex) => (
                                  <span
                                    className="ilrEvent"
                                    key={event.event_key || `${event.movement_id}-${eventIndex}`}
                                  >
                                    {event.movement_date || "No date"} · {event.reference_label || event.movement_type} · {signedQty(event.quantity_delta)}
                                  </span>
                                ))}
                                {location.events.length > 5 ? (
                                  <span className="ilrEvent">+{location.events.length - 5} more</span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>)}

                    {blockers.length ? (
                      <ul className="ilrBlockers">
                        {blockers.map((blocker, index) => (
                          <li key={`${blocker.code}-${blocker.location_id || blocker.movement_id || index}`}>
                            {blockerCopy(blocker)}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {plan.warnings?.length ? (
                      <ul className="ilrWarnings">
                        {plan.warnings.map((warning, index) => (
                          <li key={`${warning.code}-${warning.location_id || index}`}>
                            {warningCopy(warning)}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {isManualMode && !plan.manual_reset_available ? (
                      <ul className="ilrBlockers">
                        <li>
                          Manual reset unavailable: {plan.manual_reset_unavailable_reason || "the server could not establish the required audit structure."}
                        </li>
                      </ul>
                    ) : null}
                  </article>
                );
              })
            )}
          </div>
          )}

          {offset > 0 || serverHasMore ? <div className="ilrPager">
            <button
              className="ilrButton ilrButton--secondary"
              type="button"
              disabled={offset === 0 || loadingPreview || applying}
              onClick={() => goToPage(offset - pageLimit)}
            >
              Previous
            </button>
            <button
              className="ilrButton ilrButton--secondary"
              type="button"
              disabled={!serverHasMore || loadingPreview || applying}
              onClick={() => goToPage(offset + pageLimit)}
            >
              Next
            </button>
          </div> : null}

          {plans.length ? <section className="ilrPanel ilrExecution">
            {executionMode === "manual_location_reset" ? (
              <div className="ilrManualWarning">
                Administrative override: current physical and Legacy / Unallocated quantities are
                prefilled. Enter the exact final quantity in any cell that must change. Global stock
                becomes the sum of every location column, including Legacy. Editing a cell selects
                its row automatically; then apply the selected totals directly below.
              </div>
            ) : null}
            <div className="ilrExecutionHeader">
              <div>
                <h2 className="ilrExecutionTitle">
                  {executionMode === "manual_location_reset"
                    ? "Force exact location totals"
                    : "Apply selected reconciliation"}
                </h2>
                <p className="ilrExecutionNote">
                  {executionMode === "manual_location_reset"
                    ? "Each item is reset transactionally to the quantities entered above. Prior automatic reconstruction warnings are retained in the audit metadata but do not determine the new totals."
                    : "This creates an auditable current-date inventory adjustment, updates the selected physical location balances and records the before/after values in the immutable location ledger. Each item is transactional and revalidated against this preview."}
                </p>
              </div>
              <div className="ilrSafety">{selectedPlans.length} / 50 selected</div>
            </div>
            <div
              className={`ilrExecutionGrid ${executionMode === "manual_location_reset" ? "ilrExecutionGrid--manual" : ""}`}
            >
              {executionMode === "safe_reconciliation" ? (
                <>
                  <label className="ilrField">
                    <span className="ilrLabel">Audit reason</span>
                    <textarea
                      className="ilrTextarea"
                      value={reason}
                      disabled={applying}
                      placeholder="Why this one-time reconciliation is being applied"
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </label>
                  <label className="ilrField">
                    <span className="ilrLabel">Typed confirmation</span>
                    <input
                      className="ilrInput"
                      value={confirmation}
                      disabled={applying}
                      autoComplete="off"
                      placeholder={confirmationText}
                      onChange={(event) => setConfirmation(event.target.value)}
                    />
                    <div className="ilrConfirmHint">Type exactly: {confirmationText}</div>
                  </label>
                </>
              ) : null}
              <button
                className={`ilrButton ${executionMode === "manual_location_reset" ? "ilrButton--manual" : "ilrButton--danger"}`}
                type="button"
                disabled={!canApply}
                onClick={applySelected}
              >
                {applying
                  ? "Applying…"
                  : executionMode === "manual_location_reset"
                    ? "Force selected totals"
                    : "Apply selected"}
              </button>
            </div>
            {applyResults.length ? (
              <ul className="ilrResultList">
                {applyResults.map((result) => {
                  const resultPlan = plans.find(
                    (plan) => Number(plan.item_id) === Number(result.item_id)
                  );
                  const movementDate =
                    result.movement_date || result.movement?.movement_date || null;
                  const accountingDate =
                    result.accounting_date || result.movement?.accounting_date || null;
                  return (
                    <li key={result.item_id}>
                      {result.item?.code || resultPlan?.item?.code || `Item ${result.item_id}`} · {result.item?.name || resultPlan?.item?.name || "Unknown item"}: {result.ok
                        ? result.forced
                          ? `forced · global ${formatQty(result.before?.global_qty)} → ${formatQty(result.after?.global_qty)} · Legacy ${formatQty(result.after?.legacy_unallocated_qty)}`
                          : result.applied
                            ? "reconciled"
                            : "unchanged"
                        : result.error}
                      {result.ok && result.forced && Array.isArray(result.locations) ? (
                        <div className="ilrAppliedLocations">
                          {result.locations
                            .map(
                              (location) =>
                                `${location.location_name}: ${formatQty(location.before_qty)} → ${formatQty(location.after_qty)}`
                            )
                            .join(" · ")}
                        </div>
                      ) : null}
                      {result.ok && (movementDate || accountingDate) ? (
                        <div className="ilrAppliedLocations">
                          {movementDate ? `Movement date ${movementDate}` : ""}
                          {movementDate && accountingDate ? " · " : ""}
                          {accountingDate ? `Accounting date ${accountingDate}` : ""}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section> : null}
        </>
      ) : null}
    </div>
  );
}

export default InventoryLocationReconciliationAdmin;
